import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Moon, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const moonPhase = () => {
    const synodic = 29.530588853;
    const known = Date.UTC(2000, 0, 6, 18, 14) / 86400000;
    const now = Date.now() / 86400000;
    const age = ((now - known) % synodic + synodic) % synodic;
    if (age < 1.85) return "New Moon — the veil is thinnest";
    if (age < 5.54) return "Waxing Crescent — intentions take root";
    if (age < 9.23) return "First Quarter — the sisters are listening";
    if (age < 12.92) return "Waxing Gibbous — energy gathers";
    if (age < 16.61) return "Full Moon — the mirror is wide open";
    if (age < 20.3) return "Waning Gibbous — release what she kept";
    if (age < 23.99) return "Last Quarter — threads are cut clean";
    if (age < 27.68) return "Waning Crescent — rest between realms";
    return "New Moon — the veil is thinnest";
};

export const Footer = () => {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/newsletter`, { email });
            toast.success("You are now aligned with the whisper-list.");
            setEmail("");
        } catch {
            toast.error("The alignment failed. Try again under a different star.");
        } finally {
            setSending(false);
        }
    };

    return (
        <footer data-testid="footer-section" className="relative overflow-hidden border-t border-purple-500/20 px-5 pb-12 pt-20 sm:px-8">
            <div className="nebula-blob left-[30%] bottom-[-30%] h-[30rem] w-[30rem] bg-[#5a189a]/25" />
            <div className="relative mx-auto max-w-6xl">
                <h2 className="pointer-events-none select-none text-center font-display text-[13vw] font-black leading-none tracking-tight text-[#7b2cbf]/10 lg:text-[9rem]">
                    DICHOTOMY
                </h2>

                <div className="mt-4 grid gap-10 md:grid-cols-3">
                    <div className="flex items-start gap-3">
                        <img src="/assets/sisters-mark.png" alt="" className="h-10 w-10 rounded-full ring-1 ring-[#c77dff]/40" />
                        <div>
                            <p className="font-cinzel text-sm tracking-[0.25em] text-[#f8f7ff]">DICHOTOMY SISTERS</p>
                            <p className="mt-2 font-cormorant text-base italic text-[#e0aaff]/70">
                                A realm between a dream, a mirror, and another dimension.
                            </p>
                        </div>
                    </div>

                    <div data-testid="footer-moon-phase" className="flex items-start gap-3">
                        <Moon className="mt-1 h-5 w-5 text-[#e0aaff]" />
                        <div>
                            <p className="eyebrow">Current moon</p>
                            <p className="mt-2 font-cormorant text-base italic text-[#f8f7ff]/80">{moonPhase()}</p>
                        </div>
                    </div>

                    <form onSubmit={subscribe} className="flex flex-col gap-3">
                        <p className="eyebrow">Receive the whispers</p>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="footer-newsletter-input"
                                placeholder="your@email.com"
                                className="w-full rounded-full border border-purple-500/30 bg-[#0b031e]/70 px-5 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 transition-[border-color,box-shadow] duration-300 focus:border-[#c77dff]/70 focus:outline-none"
                            />
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="footer-newsletter-submit"
                                aria-label="Subscribe"
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#c77dff]/50 text-[#e0aaff] transition-[box-shadow,transform] duration-300 hover:shadow-[0_0_20px_rgba(199,125,255,0.5)] active:scale-95 disabled:opacity-60"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </div>
                    </form>
                </div>

                <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-purple-500/15 pt-8 sm:flex-row">
                    <p className="text-xs tracking-[0.2em] text-[#c77dff]/50">© 2026 DICHOTOMY SISTERS LLC</p>
                    <div className="flex items-center gap-6">
                        <a href="/terms" data-testid="footer-terms-link" className="font-cinzel text-[10px] tracking-[0.2em] text-[#e0aaff]/60 transition-colors duration-300 hover:text-[#f8f7ff]">
                            TERMS &amp; CONDITIONS
                        </a>
                        <a href="/privacy" data-testid="footer-privacy-link" className="font-cinzel text-[10px] tracking-[0.2em] text-[#e0aaff]/60 transition-colors duration-300 hover:text-[#f8f7ff]">
                            PRIVACY POLICY
                        </a>
                        <p className="text-[10px] tracking-[0.2em] text-[#c77dff]/40">CRAFTED IN THE USA</p>
                    </div>
                    <p className="font-cormorant text-sm italic text-[#e0aaff]/60">Not every mirror holds a soul.</p>
                </div>

                <div data-testid="legal-footer" className="mt-8 border-t border-purple-500/10 pt-6 text-center">
                    <p className="text-[11px] leading-relaxed tracking-wide text-[#c77dff]/40">
                        © 2026 Dichotomy Sisters LLC. All Rights Reserved.
                        <br />
                        Registered Office: 1209 North Orange Street, Wilmington, DE 19801, USA.
                    </p>
                    <p className="mx-auto mt-3 max-w-2xl text-[10px] leading-relaxed text-[#c77dff]/30">
                        Disclaimer: All readings, spell consultations, and spiritual alignments provided by Mara Vey are
                        for entertainment purposes only. Payment processing is encrypted and secured under US data
                        regulations. Must be 18 or older to purchase.
                    </p>
                </div>
            </div>
        </footer>
    );
};
